<template>
  <div class="dashboard-layout">
    <!-- Ringkasan & Grafik -->
    <div class="summary-section">
      <div v-if="summaryLoading">Memuat ringkasan...</div>
      <div v-if="summaryData" class="summary-grid">
        <div class="summary-card">
          <h4>Total Aset</h4>
          <p>Rp {{ formatCurrency(summaryData.total_assets) }}</p>
        </div>
        <div class="summary-card liabilities">
          <h4>Total Pinjaman</h4>
          <p>Rp {{ formatCurrency(summaryData.total_liabilities) }}</p>
        </div>
        <div class="summary-card net-worth">
          <h4>Kekayaan Bersih</h4>
          <p>Rp {{ formatCurrency(summaryData.net_worth) }}</p>
        </div>
        <div class="chart-container">
          <SummaryChart :summary="summaryData" />
        </div>
      </div>
    </div>

    <!-- Navigasi Tab -->
    <div class="tabs">
      <button @click="activeTab = 'accounts'" :class="{ active: activeTab === 'accounts' }">Rekening Saya</button>
      <button @click="activeTab = 'transfer'" :class="{ active: activeTab === 'transfer' }">Transfer</button>
      <button @click="activeTab = 'repayLoan'" v-if="loanAccounts.length > 0">Bayar Pinjaman</button>
      <button @click="activeTab = 'addAccount'" :class="{ active: activeTab === 'addAccount' }">Buka Rekening</button>
      <button @click="activeTab = 'applyLoan'" :class="{ active: activeTab === 'applyLoan' }">Ajukan Pinjaman</button>
    </div>

    <!-- Konten Tab -->
    <div class="main-panel">
      <!-- Tab: Rekening Saya -->
      <div v-show="activeTab === 'accounts'">
        <div v-if="error" class="error-message">{{ error }}</div>
        <div class="account-section">
          <h2>Rekening Simpanan</h2>
          <div v-if="loading">Memuat...</div>
          <div v-else-if="savingsAccounts.length === 0">Anda belum memiliki rekening simpanan.</div>
          <ul v-else class="accounts-list">
            <li v-for="account in savingsAccounts" :key="account.id" class="account-item">
              <router-link :to="{ name: 'AccountDetail', params: { id: account.id }, query: { account: JSON.stringify(account) } }" class="account-item-link">
                <div class="account-info">
                  <span class="account-type">{{ account.account_type }}</span>
                  <span class="account-number">{{ account.account_number }}</span>
                </div>
                <div class="account-status-balance">
                  <span class="status-badge" :class="`status-${account.status.toLowerCase()}`">{{ account.status }}</span>
                  <div class="account-balance">Rp {{ formatCurrency(account.balance) }}</div>
                </div>
              </router-link>
            </li>
          </ul>
        </div>

        <div v-if="loanAccounts.length > 0" class="account-section">
          <h2>Rekening Pinjaman</h2>
          <ul class="accounts-list">
            <li v-for="account in loanAccounts" :key="account.id" class="account-item loan">
              <router-link :to="{ name: 'AccountDetail', params: { id: account.id }, query: { account: JSON.stringify(account) } }" class="account-item-link">
                <div class="account-info">
                  <span class="account-type">{{ account.account_type }}</span>
                  <span class="account-number">{{ account.account_number }}</span>
                </div>
                <div class="account-status-balance">
                  <div class="account-balance">Rp {{ formatCurrency(account.balance) }}</div>
                </div>
              </router-link>
            </li>
          </ul>
        </div>
      </div>

      <div v-if="activeTab === 'transfer'">
        <h2>Transfer Dana</h2>
        <TransferForm
          v-if="!loading && accounts.length > 0"
          :user-accounts="accounts"
          @transfer-success="refreshData"
        />
        <div v-else-if="!loading" class="no-accounts">Anda perlu memiliki rekening untuk melakukan transfer.</div>
      </div>

      <div v-show="activeTab === 'repayLoan'">
        <h2>Pembayaran Pinjaman</h2>
        <LoanRepaymentForm 
          :active-loans="myLoans.filter(l => l.status === 'Disetujui')"
          :savings-accounts="savingsAccounts"
          @repayment-success="refreshData"
        />
      </div>

      <!-- Tab: Buka Rekening -->
      <div v-if="activeTab === 'addAccount'">
        <h2>Buka Rekening Baru</h2>
        <AddAccountForm @account-added="refreshData" />
      </div>

      <!-- Tab: Ajukan Pinjaman -->
      <div v-if="activeTab === 'applyLoan'">
        <h2>Ajukan Pinjaman</h2>
        <LoanApplicationForm
          v-if="!loading && accounts.length > 0"
          :user-accounts="accounts"
          @loan-submitted="refreshData"
        />
        <div v-else-if="!loading" class="no-accounts">Anda perlu memiliki rekening untuk mengajukan pinjaman.</div>
      </div>

    </div>
  </div>
</template>


<script setup>
import { ref, onMounted, computed } from 'vue'; // <-- IMPOR 'computed'
import { useDashboardStore } from '@/store/dashboard';
import { storeToRefs } from 'pinia';
import api from '@/services/api';

import TransferForm from '@/components/TransferForm.vue';
import AddAccountForm from '@/components/AddAccountForm.vue';
import LoanApplicationForm from '@/components/LoanApplicationForm.vue';
import SummaryChart from '@/components/SummaryChart.vue';
import LoanRepaymentForm from '@/components/LoanRepaymentForm.vue';

const dashboardStore = useDashboardStore();
const { accounts, loading, error } = storeToRefs(dashboardStore);

const activeTab = ref('accounts');
const summaryData = ref(null);
const summaryLoading = ref(true);
const myLoans = ref([]);

const savingsAccounts = computed(() => accounts.value.filter(acc => acc.account_type !== 'Pinjaman'));
const loanAccounts = computed(() => accounts.value.filter(acc => acc.account_type === 'Pinjaman'));

const formatCurrency = (value) => {
  const num = parseFloat(value);
  return isNaN(num) ? '0.00' : num.toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Fetch data
const fetchSummary = async () => {
  summaryLoading.value = true;
  try {
    const response = await api.getDashboardSummary();
    summaryData.value = response.data;
  } catch (e) {
    console.error('Gagal memuat ringkasan:', e);
  } finally {
    summaryLoading.value = false;
  }
};

const fetchMyLoans = async () => {
  try {
    const response = await api.getMyLoans();
    myLoans.value = response.data;
  } catch (e) {
    console.error('Gagal memuat data pinjaman:', e);
  }
};

const refreshData = async () => {
  console.log('Me-refresh data dasbor...');
  try {
    await Promise.all([
      dashboardStore.fetchAccounts(),
      fetchMyLoans()
    ]);
  } catch (e) {
    console.error('Gagal me-refresh data:', e);
  }
};

onMounted(() => {
  refreshData();
  fetchSummary();
});
</script>

<style scoped>
.dashboard-layout {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
}

/* Ringkasan */
.summary-section {
  margin-bottom: 1.5rem;
}
.summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}
.summary-card {
  padding: 1rem;
  background: white;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
}
.summary-card h4 {
  margin-bottom: 0.5rem;
  color: #555;
}
.summary-card p {
  font-size: 1.5rem;
  font-weight: bold;
}
.summary-card.liabilities p {
  color: #dc3545;
}
.summary-card.net-worth p {
  color: #007bff;
}
.chart-container {
  grid-column: 1 / -1;
  height: 300px;
  margin-top: 1rem;
  background: white;
  padding: 1rem;
  border-radius: 8px;
}

/* Tab Navigation */
.tabs {
  display: flex;
  border-bottom: 2px solid #ddd;
  margin-bottom: 1.5rem;
}
.tabs button {
  padding: 1rem 1.5rem;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 1rem;
  position: relative;
  color: #555;
}
.tabs button.active {
  font-weight: bold;
  color: #007bff;
}
.tabs button.active::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 100%;
  height: 2px;
  background-color: #007bff;
}

/* Main Panel */
.main-panel {
  width: 100%;
}

/* Rekening List */
.accounts-list {
  list-style: none;
  padding: 0;
}
.account-item-link {
  text-decoration: none;
  color: inherit;
}
.account-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  margin-bottom: 0.75rem;
}
.account-info .account-type {
  font-weight: bold;
  display: block;
}
.account-info .account-number {
  color: #666;
  font-size: 0.9rem;
}

/* Pinjaman */
.my-loans-container {
  margin-top: 2rem;
  padding: 1.5rem;
  background-color: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
}
.loans-list {
  list-style: none;
  padding: 0;
  margin-top: 1rem;
}
.loan-item {
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  border-bottom: 1px solid #f0f0f0;
}
.loan-item:last-child {
  border-bottom: none;
}
.status-diajukan {
  color: #ffc107;
}
.status-disetujui {
  color: #28a745;
}
.status-ditolak-awal,
.status-ditolak-akhir {
  color: #dc3545;
}

/* Umum */
.loading,
.error-message,
.no-accounts {
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  margin: 1rem 0;
}
.error-message {
  color: #dc3545;
}

/* Responsive */
@media (max-width: 768px) {
  .tabs {
    flex-wrap: wrap;
  }
  .tabs button {
    flex: 1 0 45%;
    margin-bottom: 0.5rem;
  }
}
.account-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    /* ... */
  }
  
  /* Style baru untuk mengelompokkan status dan saldo */
  .account-status-balance {
    text-align: right;
  }
  
  .status-badge {
    display: inline-block;
    padding: 0.25em 0.6em;
    font-size: 0.75rem;
    font-weight: 700;
    line-height: 1;
    text-align: center;
    white-space: nowrap;
    vertical-align: baseline;
    border-radius: 0.25rem;
    color: #fff;
    margin-bottom: 0.25rem;
  }
  
  .status-active {
    background-color: #28a745; /* Hijau */
  }
  
  .status-pending {
    background-color: #ffc107; /* Kuning */
    color: #212529; /* Teks gelap agar mudah dibaca */
  }
  
  .status-rejected {
    background-color: #dc3545; /* Merah */
  }
  
  .status-suspended {
    background-color: #6c757d; /* Abu-abu */
  }

  /* Sesuaikan margin saldo */
  .account-balance {
    font-size: 1.2em;
    font-weight: bold;
    color: #2c3e50;
  }
/* Tingkatkan spesifisitas dengan menargetkan dari elemen induk */
.account-item .status-badge {
  display: inline-block;
  padding: 0.25em 0.6em;
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1;
  text-align: center;
  white-space: nowrap;
  vertical-align: baseline;
  border-radius: 0.25rem;
  color: #fff; /* Teks putih default */
}
  
.account-item .status-active {
  background-color: #28a745; /* Hijau */
}
  
.account-item .status-pending {
  background-color: #ffc107; /* Kuning */
  color: #212529; /* Teks gelap */
}
  
.account-item .status-rejected {
  background-color: #dc3545; /* Merah */
}
  
.account-item .status-suspended {
  background-color: #6c757d; /* Abu-abu */
}

/* Style untuk tombol "Lihat Riwayat" agar lebih rapi */
.view-history-btn {
  padding: 0.4rem 0.8rem;
  border: 1px solid #ddd;
  background-color: #f8f9fa;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  color: #333;
  transition: background-color 0.2s;
}
.view-history-btn:hover {
  background-color: #e9ecef;
}
</style>